import { useOrganization } from 'core/stores/hooks/useOrganization';
import { extractListItems } from 'core/utils/apollo/extractListItems';
import {
  useGetCandidateBatchForCampaignCreationQuery,
  useGetSelectedCandidateBatchStatusesByCampaignQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import { useEffect } from 'react';
import { useUpsertSelectedCandidateBatchStatuses } from 'modules/campaign-creation/hooks/useUpsertSelectedCandidateBatchStatuses';
import { CandidateStatus } from 'generated-graphql/types';
import { getAnalysisTitle } from 'utils/getAnalysisTitle';

export const useSelectedCandidateBatchStatusesHandler = () => {
  const campaignId = useCampaignCreationStore((store) => store.campaignId);
  const state = useCampaignCreationStore((store) => store.state);
  const organization = useOrganization();
  const { upsertSelectedCandidateBatchStatuses } =
    useUpsertSelectedCandidateBatchStatuses();
  const onChooseRecipientsSubmit = useCampaignCreationStore(
    (store) => store.onChooseRecipientsSubmit
  );
  const { data: candidateBatchData, loading: candidateBatchLoading } =
    useGetCandidateBatchForCampaignCreationQuery({
      variables: {
        atsJobId: state?.ATSJobDescriptionState?.selectedJob?.atsJobId || ''
      },
      skip: !state?.ATSJobDescriptionState?.selectedJob?.atsJobId
    });
  const {
    data: selectedCandidateBatchStatusesData,
    loading: selectedCandidateBatchStatusesLoading,
    refetch: refetchSelectedCandidateBatchStatuses
  } = useGetSelectedCandidateBatchStatusesByCampaignQuery({
    variables: {
      campaignId: campaignId || ''
    },
    skip: !campaignId
  });

  const candidateBatch = extractListItems(
    candidateBatchData?.getCandidateBatchByStatusAndAtsJobId?.items || []
  )[0];

  const selectedCandidateBatchStatuses = extractListItems(
    selectedCandidateBatchStatusesData
      ?.getSelectedCandidateBatchStatusesByCampaign?.items || []
  )[0];

  useEffect(() => {
    const createSelectedCandidateBatchStatuses = async () => {
      if (!campaignId) {
        return;
      }

      if (candidateBatchLoading || selectedCandidateBatchStatusesLoading) {
        return;
      }

      if (selectedCandidateBatchStatuses) {
        return;
      }

      if (!candidateBatch) {
        return;
      }

      await upsertSelectedCandidateBatchStatuses({
        id: undefined,
        organizationId: organization.id,
        campaignId,
        candidateBatchId: candidateBatch.id,
        candidateStatuses: []
      });

      await refetchSelectedCandidateBatchStatuses();
    };

    createSelectedCandidateBatchStatuses();
  }, [candidateBatch, selectedCandidateBatchStatuses]);

  const onUpdateSelectedCandidateBatchId = async (candidateBatchId: string) => {
    if (!campaignId) {
      return;
    }

    await upsertSelectedCandidateBatchStatuses({
      id: selectedCandidateBatchStatuses?.id,
      candidateBatchId,
      campaignId,
      organizationId: organization.id,
      candidateStatuses: selectedCandidateBatchStatuses?.candidateStatuses || []
    });

    refetchSelectedCandidateBatchStatuses();
  };

  const onUpdateSelectedCandidateBatchStatuses = async (
    candidateStatuses: CandidateStatus[]
  ) => {
    if (!campaignId) {
      return;
    }

    await upsertSelectedCandidateBatchStatuses({
      id: selectedCandidateBatchStatuses?.id,
      candidateBatchId: selectedCandidateBatchStatuses?.candidateBatchId,
      campaignId,
      organizationId: organization.id,
      candidateStatuses
    });
  };

  const onSelectEveryone = async () => {
    if (!campaignId) {
      return;
    }

    if (selectedCandidateBatchStatuses?.id) {
      await upsertSelectedCandidateBatchStatuses({
        id: selectedCandidateBatchStatuses?.id,
        candidateStatuses: []
      });
    }

    onChooseRecipientsSubmit({
      recipientType: 'everyone'
    });
  };

  const onSelectImport = async () => {
    if (!campaignId) {
      return;
    }

    if (selectedCandidateBatchStatuses?.id) {
      await upsertSelectedCandidateBatchStatuses({
        id: selectedCandidateBatchStatuses?.id,
        candidateStatuses: []
      });
    }

    onChooseRecipientsSubmit({
      recipientType: 'imported'
    });
  };

  return {
    loading: candidateBatchLoading || selectedCandidateBatchStatusesLoading,
    selectedCandidateBatchStatusesId: selectedCandidateBatchStatuses?.id,
    candidateBatchId: selectedCandidateBatchStatuses?.candidateBatchId,
    candidateBatchName: getAnalysisTitle(
      selectedCandidateBatchStatuses?.candidateBatch,
      'No analysis linked'
    ),
    candidateBatchStatuses: selectedCandidateBatchStatuses?.candidateStatuses,
    onUpdateSelectedCandidateBatchId,
    onUpdateSelectedCandidateBatchStatuses,
    onSelectEveryone,
    onSelectImport
  };
};
